/**
 * SlapTaffy Animation - "Ultimate Slap Attack" (ENHANCED VERSION)
 * 
 * FRAMER MOTION VERSION: Ultimate cartoon slap experience!
 * - Windup anticipation with taffy flexing (tier 3+)
 * - Approaching hand with finger wiggle animation
 * - Red handprint slap mark that fades
 * - Dizzy spiral eyes on impact (tier 4+)
 * - Multi-hit combo for high tiers (tier 5+)
 * - Manga word art variations (POW!, WAM!, SLAP!, etc.)
 * - Enhanced motion trails with blur
 * - Angry face on taffy during wobbling
 * - Flying sweat drops
 * 
 * Synchronized with sound effects.
 */

import React, { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import useAnimationSound from '../../../hooks/useAnimationSound';
import { getPhasesForCandy } from './soundRegistry';

const SlapTaffyAnimation = ({ intensity = 1, onComplete }) => {
    const [phase, setPhase] = useState('winding');

    // Sound integration
    const { playPhaseSound, preloadSounds } = useAnimationSound('SlapTaffy', intensity, true);

    // Random slap text pool - expanded
    const slapTexts = useMemo(() => ['SLAP!', 'SMACK!', 'THWACK!', 'WHAP!', 'POW!', 'WAM!', 'KAPOW!', 'BONK!'], []);
    const slapText = useMemo(() => slapTexts[Math.floor(Math.random() * slapTexts.length)], [slapTexts]);

    // Device-aware particle scaling for performance
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const particleMultiplier = isMobile ? 0.4 : 1;

    // Scaled effect counts with mobile optimization
    const speedLineCount = Math.floor((8 + intensity * 3) * particleMultiplier);
    const rippleCount = Math.floor((4 + intensity) * particleMultiplier);
    const starCount = Math.floor((5 + intensity * 2) * particleMultiplier);

    // Tier-based features
    const slapEchoes = intensity >= 4 ? intensity - 3 : 0;
    const showSpeedLines = true;
    const showHand = intensity >= 2;
    const showComicStars = intensity >= 2;
    const showComboCounter = intensity >= 5;
    const showVibration = intensity >= 4;
    const showEpicShake = intensity >= 5;
    const showCheekMark = intensity >= 3;
    const showWindupFlex = intensity >= 3;
    const showDizzyEyes = intensity >= 4;
    const showMultiHit = intensity >= 5;
    const showAngryFace = true;
    const showSweatDrops = intensity >= 3;
    const showHandprint = intensity >= 3;

    // Animation timing
    const baseTiming = intensity >= 5 ? 1.2 : 1;

    // Stable ref for playPhaseSound to avoid re-triggering animation useEffect
    const playPhaseSoundRef = React.useRef(playPhaseSound);
    playPhaseSoundRef.current = playPhaseSound;

    // Stable ref for onComplete
    const onCompleteRef = React.useRef(onComplete);
    onCompleteRef.current = onComplete;

    // Preload sounds (only once on mount)
    useEffect(() => {
        preloadSounds(getPhasesForCandy('SlapTaffy'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Extended animation sequence with more phases
    useEffect(() => {
        const timers = [];

        playPhaseSoundRef.current('winding');

        timers.push(setTimeout(() => {
            setPhase('slapping');
            playPhaseSoundRef.current('slapping');
        }, 320 * baseTiming));

        timers.push(setTimeout(() => {
            setPhase('impact');
            playPhaseSoundRef.current('impact');
        }, 500 * baseTiming));

        timers.push(setTimeout(() => {
            setPhase('wobbling');
            playPhaseSoundRef.current('wobbling');
        }, 720 * baseTiming));

        timers.push(setTimeout(() => {
            setPhase('complete');
            onCompleteRef.current?.();
        }, 2100 * baseTiming));

        return () => timers.forEach(clearTimeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Generate effects
    const speedLines = useMemo(() =>
        Array.from({ length: speedLineCount }, (_, i) => ({
            id: i,
            y: -90 + (180 / speedLineCount) * i + Math.random() * 12,
            length: 70 + Math.random() * 100,
            delay: i * 0.012,
            opacity: 0.5 + Math.random() * 0.4,
            thickness: 2 + Math.random() * 2,
        })), [speedLineCount]);

    const ripples = useMemo(() =>
        Array.from({ length: rippleCount }, (_, i) => ({
            id: i,
            delay: i * 0.06,
            size: 130 + i * 45,
        })), [rippleCount]);

    const stars = useMemo(() =>
        Array.from({ length: starCount }, (_, i) => ({
            id: i,
            angle: (360 / starCount) * i + Math.random() * 25,
            distance: 55 + Math.random() * 70,
            delay: Math.random() * 0.18,
            size: 14 + Math.random() * 14,
        })), [starCount]);

    const sweatDrops = useMemo(() => [
        { id: 0, x: -25, y: -10, delay: 0 },
        { id: 1, x: 25, y: -8, delay: 0.1 },
        { id: 2, x: -15, y: 5, delay: 0.2 },
    ], []);

    // Multi-hit text positions
    const multiHitPositions = useMemo(() => [
        { x: 30, y: -40, rotation: 10 },
        { x: -35, y: -30, rotation: -15 },
        { x: 25, y: 20, rotation: 5 },
    ], []);

    return (
        <div
            className={`relative flex items-center justify-center overflow-visible
                w-48 h-48 sm:w-56 sm:h-56 md:w-64 md:h-64
                ${showVibration && phase === 'impact' ? 'animate-vibrate' : ''}
                ${showEpicShake && phase === 'impact' ? 'animate-shake-epic' : ''}
            `}
        >
            {/* === SPEED LINES (enhanced) === */}
            <AnimatePresence>
                {showSpeedLines && (phase === 'slapping' || phase === 'impact') && (
                    <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
                        {speedLines.map((line) => (
                            <motion.div
                                key={line.id}
                                className="absolute right-0"
                                style={{
                                    width: line.length,
                                    height: line.thickness,
                                    top: `calc(50% + ${line.y}px)`,
                                    background: `linear-gradient(to left, rgba(255,105,180,${line.opacity}), transparent)`,
                                    filter: 'blur(0.5px)',
                                }}
                                initial={{ scaleX: 0, x: 60 }}
                                animate={{ scaleX: 1, x: -120 }}
                                exit={{ opacity: 0 }}
                                transition={{ delay: line.delay, duration: 0.18 }}
                            />
                        ))}
                    </div>
                )}
            </AnimatePresence>

            {/* === CARTOON HAND (enhanced with finger wiggle) === */}
            <AnimatePresence>
                {showHand && (phase === 'slapping' || phase === 'impact') && (
                    <motion.div
                        className="absolute"
                        style={{ right: -35, zIndex: 10 }}
                        initial={{ x: 120, rotate: 35 }}
                        animate={
                            phase === 'slapping' ? { x: 0, rotate: 0 } :
                                phase === 'impact' ? { x: -25, rotate: -12 } : {}
                        }
                        exit={{ x: 120, opacity: 0 }}
                        transition={{ duration: 0.12, type: 'spring', stiffness: 350 }}
                    >
                        {/* Palm */}
                        <div
                            className="w-18 h-14 rounded-lg relative"
                            style={{
                                width: '4.5rem',
                                background: 'linear-gradient(135deg, #FFE4C4 0%, #F4A460 100%)',
                                boxShadow: '0 5px 15px rgba(0,0,0,0.25)',
                            }}
                        >
                            {/* Fingers with wiggle animation */}
                            <div className="absolute -top-9 left-1 flex gap-1">
                                {[0, 1, 2, 3].map((i) => (
                                    <motion.div
                                        key={i}
                                        className="w-3.5 h-11 rounded-t-full"
                                        style={{
                                            background: 'linear-gradient(135deg, #FFE4C4 0%, #F4A460 100%)',
                                            transformOrigin: 'bottom center',
                                        }}
                                        animate={phase === 'slapping' ? {
                                            rotate: [0, -8 + i * 3, 0],
                                        } : {}}
                                        transition={{ duration: 0.1, delay: i * 0.02 }}
                                    />
                                ))}
                            </div>
                            {/* Thumb */}
                            <div
                                className="absolute -left-5 top-0 w-5 h-9 rounded-l-full"
                                style={{
                                    background: 'linear-gradient(135deg, #FFE4C4 0%, #F4A460 100%)',
                                    transform: 'rotate(-25deg)',
                                }}
                            />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* === TAFFY CANDY (enhanced with expressions) === */}
            <motion.div
                className="relative"
                animate={
                    phase === 'winding' ? {
                        scale: showWindupFlex ? [0.9, 1.1, 0.95, 1.05, 1] : [0.9, 1],
                        x: 0,
                    } :
                        phase === 'slapping' ? { x: -12 } :
                            phase === 'impact' ? { x: 35, scaleX: 0.75 } :
                                phase === 'wobbling' ? {
                                    x: [35, -15, 8, -5, 2, 0],
                                    scaleX: [0.75, 1.15, 0.92, 1.05, 0.98, 1],
                                    rotate: [0, -8, 5, -3, 1, 0],
                                } : {}
                }
                transition={
                    phase === 'winding' ? { duration: 0.4, ease: 'easeInOut' } :
                        phase === 'wobbling' ? { duration: 0.9, ease: 'easeOut' } :
                            { duration: 0.12, type: 'spring', stiffness: 280 }
                }
            >
                <motion.div
                    className="w-22 h-16 rounded-lg relative overflow-hidden"
                    style={{
                        width: '5.5rem',
                        height: '4rem',
                        background: 'linear-gradient(135deg, #FF69B4 0%, #FF1493 50%, #C71585 100%)',
                        boxShadow: '0 5px 20px rgba(255, 20, 147, 0.45)',
                    }}
                >
                    {/* Stripes */}
                    {[0, 1, 2, 3].map((i) => (
                        <div
                            key={i}
                            className="absolute h-full w-3"
                            style={{
                                background: 'rgba(255,255,255,0.22)',
                                left: `${15 + i * 22}%`,
                                transform: 'skewX(-12deg)',
                            }}
                        />
                    ))}

                    {/* Shine */}
                    <div
                        className="absolute w-10 h-5 rounded-full"
                        style={{
                            background: 'rgba(255,255,255,0.45)',
                            top: '12%',
                            left: '12%',
                            filter: 'blur(3px)',
                        }}
                    />

                    {/* === EXPRESSIVE FACE === */}
                    <AnimatePresence>
                        {showAngryFace && (
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                {/* Eyes */}
                                <div className="flex gap-3 mb-1">
                                    {[0, 1].map((i) => (
                                        <div key={i} className="relative">
                                            {/* Angry eyebrows (during winding) */}
                                            {phase === 'winding' && (
                                                <motion.div
                                                    className="absolute -top-2 w-4 h-1 bg-[#8B0A50] rounded"
                                                    style={{
                                                        transformOrigin: i === 0 ? 'right' : 'left',
                                                    }}
                                                    initial={{ rotate: i === 0 ? -20 : 20 }}
                                                    animate={{ rotate: i === 0 ? -30 : 30 }}
                                                    transition={{ duration: 0.2, repeat: Infinity, repeatType: 'reverse' }}
                                                />
                                            )}
                                            {/* Eye - normal or dizzy */}
                                            <motion.div
                                                className="w-4 h-4 bg-white rounded-full flex items-center justify-center overflow-hidden"
                                                style={{ boxShadow: 'inset 0 2px 3px rgba(0,0,0,0.2)' }}
                                                animate={phase === 'impact' ? { scaleY: 0.7 } : {}}
                                            >
                                                {showDizzyEyes && (phase === 'impact' || phase === 'wobbling') ? (
                                                    <motion.div
                                                        className="text-[10px] font-bold text-[#8B0A50]"
                                                        animate={{ rotate: 720 }}
                                                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                                                    >
                                                        @
                                                    </motion.div>
                                                ) : (
                                                    <motion.div
                                                        className="w-2 h-2 bg-[#8B0A50] rounded-full"
                                                        animate={phase === 'winding' ? {
                                                            y: [0, -1, 0],
                                                            scale: [1, 1.1, 1],
                                                        } : phase === 'impact' ? { scale: 0.6 } : {}}
                                                        transition={{ duration: 0.15, repeat: phase === 'winding' ? Infinity : 0 }}
                                                    />
                                                )}
                                            </motion.div>
                                        </div>
                                    ))}
                                </div>
                                {/* Mouth - expressions */}
                                <motion.div
                                    className="relative"
                                    animate={{
                                        scale: phase === 'impact' ? 1.5 : 1,
                                    }}
                                >
                                    {phase === 'winding' ? (
                                        <motion.div
                                            className="w-5 h-2 bg-[#8B0A50] rounded-t-full"
                                            animate={{ scaleX: [1, 0.8, 1], scaleY: [1, 1.2, 1] }}
                                            transition={{ duration: 0.2, repeat: Infinity }}
                                        />
                                    ) : phase === 'impact' ? (
                                        <motion.div
                                            className="w-6 h-4 bg-[#8B0A50] rounded-full"
                                            initial={{ scale: 0 }}
                                            animate={{ scale: [0, 1.3, 1] }}
                                            transition={{ duration: 0.1 }}
                                        />
                                    ) : (
                                        <motion.div
                                            className="w-4 h-1 bg-[#8B0A50] rounded"
                                            animate={phase === 'wobbling' ? {
                                                scaleX: [1, 0.7, 1.2, 0.9, 1],
                                            } : {}}
                                            transition={{ duration: 0.5 }}
                                        />
                                    )}
                                </motion.div>
                            </div>
                        )}
                    </AnimatePresence>

                    {/* === HANDPRINT MARK === */}
                    <AnimatePresence>
                        {showHandprint && (phase === 'impact' || phase === 'wobbling') && (
                            <motion.div
                                className="absolute inset-0 flex items-center justify-end pr-1"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: [0, 0.6, 0.4] }}
                                transition={{ duration: 0.8 }}
                            >
                                <div
                                    className="w-8 h-10 relative"
                                    style={{ opacity: 0.5 }}
                                >
                                    {/* Red handprint shape */}
                                    <div className="absolute w-7 h-6 bg-red-500/60 rounded-lg bottom-0" />
                                    {[0, 1, 2, 3].map((i) => (
                                        <div
                                            key={i}
                                            className="absolute w-1.5 h-3 bg-red-500/50 rounded-t-full"
                                            style={{
                                                left: 2 + i * 6,
                                                top: 0,
                                            }}
                                        />
                                    ))}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Cheek Mark - enhanced */}
                    <AnimatePresence>
                        {showCheekMark && (phase === 'impact' || phase === 'wobbling') && (
                            <motion.div
                                className="absolute w-10 h-7 rounded-full"
                                style={{
                                    background: 'radial-gradient(circle, rgba(255,0,0,0.45) 0%, transparent 70%)',
                                    right: '5%',
                                    top: '25%',
                                }}
                                initial={{ opacity: 0, scale: 0 }}
                                animate={{ opacity: 1, scale: [0, 1.2, 1] }}
                                transition={{ delay: 0.05, duration: 0.2 }}
                            />
                        )}
                    </AnimatePresence>
                </motion.div>

                {/* Twisted Ends */}
                <motion.div
                    className="absolute w-7 h-12 -left-6 top-1/2 -translate-y-1/2"
                    style={{
                        background: 'linear-gradient(90deg, #C71585 0%, #FF69B4 100%)',
                        borderRadius: '5px 0 0 5px',
                        clipPath: 'polygon(100% 15%, 0% 50%, 100% 85%)',
                    }}
                    animate={phase === 'impact' ? { scaleX: 0.8, x: 5 } : {}}
                />
                <motion.div
                    className="absolute w-7 h-12 -right-6 top-1/2 -translate-y-1/2"
                    style={{
                        background: 'linear-gradient(270deg, #C71585 0%, #FF69B4 100%)',
                        borderRadius: '0 5px 5px 0',
                        clipPath: 'polygon(0% 15%, 100% 50%, 0% 85%)',
                    }}
                    animate={phase === 'impact' ? { scaleX: 1.2, x: 8 } : {}}
                />

                {/* === SWEAT DROPS === */}
                <AnimatePresence>
                    {showSweatDrops && (phase === 'winding' || phase === 'slapping') && sweatDrops.map((drop) => (
                        <motion.div
                            key={drop.id}
                            className="absolute w-2 h-3"
                            style={{
                                left: `calc(50% + ${drop.x}px)`,
                                top: `calc(20% + ${drop.y}px)`,
                                background: 'linear-gradient(180deg, rgba(135,206,250,0.8) 0%, rgba(65,105,225,0.6) 100%)',
                                borderRadius: '50% 50% 50% 50% / 30% 30% 70% 70%',
                            }}
                            initial={{ y: 0, opacity: 0, scale: 0 }}
                            animate={{ y: [0, 15], opacity: [0, 1, 0], scale: [0, 1, 0.5] }}
                            transition={{ duration: 0.6, delay: drop.delay, repeat: Infinity, repeatDelay: 0.4 }}
                        />
                    ))}
                </AnimatePresence>

                {/* === MOTION TRAILS (enhanced) === */}
                <AnimatePresence>
                    {(phase === 'slapping' || phase === 'impact') && (
                        <>
                            {Array.from({ length: 6 + intensity }).map((_, i) => (
                                <motion.div
                                    key={i}
                                    className="absolute w-22 h-16 rounded-lg"
                                    style={{
                                        width: '5.5rem',
                                        height: '4rem',
                                        background: 'linear-gradient(135deg, rgba(255,105,180,0.35) 0%, transparent 100%)',
                                        right: (i + 1) * 16,
                                        filter: 'blur(2px)',
                                        opacity: 1 - (i / (6 + intensity)),
                                    }}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 - (i / (6 + intensity)) }}
                                    transition={{ delay: i * 0.012 }}
                                />
                            ))}
                        </>
                    )}
                </AnimatePresence>
            </motion.div>

            {/* === IMPACT EFFECTS === */}
            <AnimatePresence>
                {(phase === 'impact' || phase === 'wobbling') && (
                    <>
                        {/* Shockwave */}
                        <motion.div
                            className="absolute w-36 h-36 rounded-full border-4 border-pink-400"
                            initial={{ scale: 0.4, opacity: 1 }}
                            animate={{ scale: 2.2, opacity: 0 }}
                            transition={{ duration: 0.35 }}
                        />

                        {/* Ripples */}
                        {ripples.map((r) => (
                            <motion.div
                                key={r.id}
                                className="absolute rounded-full border-2 border-pink-300"
                                style={{ width: r.size, height: r.size }}
                                initial={{ scale: 0.2, opacity: 0.9 }}
                                animate={{ scale: 1.6, opacity: 0 }}
                                transition={{ delay: r.delay, duration: 0.45 }}
                            />
                        ))}

                        {/* Impact Star */}
                        <motion.div
                            className="absolute"
                            initial={{ scale: 0 }}
                            animate={{ scale: [0, 1.6, 1.1], rotate: [0, 200] }}
                            transition={{ duration: 0.28 }}
                        >
                            <div
                                className="w-18 h-18"
                                style={{
                                    width: '4.5rem',
                                    height: '4.5rem',
                                    background: '#FFD700',
                                    clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
                                    boxShadow: '0 0 25px #FFD700',
                                }}
                            />
                        </motion.div>

                        {/* Comic Stars */}
                        {showComicStars && stars.map((s) => (
                            <motion.div
                                key={s.id}
                                className="absolute"
                                style={{
                                    width: s.size,
                                    height: s.size,
                                    background: '#FFD700',
                                    clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
                                    boxShadow: '0 0 8px #FFD700',
                                }}
                                initial={{ x: 0, y: 0, scale: 0, rotate: 0 }}
                                animate={{
                                    x: Math.cos((s.angle * Math.PI) / 180) * s.distance,
                                    y: Math.sin((s.angle * Math.PI) / 180) * s.distance,
                                    scale: [0, 1.2, 0],
                                    rotate: 400,
                                }}
                                transition={{ delay: s.delay, duration: 0.55 }}
                            />
                        ))}

                        {/* Slap Echoes */}
                        {slapEchoes > 0 && Array.from({ length: slapEchoes }).map((_, i) => (
                            <motion.div
                                key={i}
                                className="absolute text-2xl font-bold text-pink-500/60"
                                style={{
                                    fontFamily: 'Impact, sans-serif',
                                    right: 25 + i * 35,
                                    top: -25 - i * 18,
                                }}
                                initial={{ opacity: 0, scale: 0.4 }}
                                animate={{ opacity: [0, 0.7, 0], scale: 1 - i * 0.12 }}
                                transition={{ delay: 0.12 + i * 0.1 }}
                            >
                                {slapText}
                            </motion.div>
                        ))}

                        {/* Multi-hit Combo (tier 5+) */}
                        {showMultiHit && multiHitPositions.map((pos, i) => (
                            <motion.div
                                key={`multi-${i}`}
                                className="absolute text-lg font-bold"
                                style={{
                                    color: ['#FF6B9D', '#FFE066', '#A78BFA'][i],
                                    textShadow: '2px 2px 0 #000',
                                    fontFamily: 'Impact, sans-serif',
                                    transform: `rotate(${pos.rotation}deg)`,
                                }}
                                initial={{ x: 0, y: 0, opacity: 0, scale: 0 }}
                                animate={{
                                    x: pos.x,
                                    y: pos.y,
                                    opacity: [0, 1, 0],
                                    scale: [0, 1.2, 0.8],
                                }}
                                transition={{ delay: 0.15 + i * 0.18, duration: 0.5 }}
                            >
                                {slapTexts[(i + 1) % slapTexts.length]}
                            </motion.div>
                        ))}
                    </>
                )}
            </AnimatePresence>

            {/* === MAIN SLAP TEXT === */}
            <AnimatePresence>
                {intensity >= 2 && phase === 'impact' && (
                    <motion.div
                        className="absolute text-5xl font-bold z-20"
                        style={{
                            color: '#FF1493',
                            textShadow: '4px 4px 0 #FFD700, -4px -4px 0 #FFD700, 4px -4px 0 #FFD700, -4px 4px 0 #FFD700, 0 0 20px rgba(255,20,147,0.5)',
                            fontFamily: 'Impact, sans-serif',
                        }}
                        initial={{ scale: 0, rotate: -25 }}
                        animate={{ scale: [0, 1.5, 1.2], rotate: [0, 12, 0] }}
                        transition={{ duration: 0.28 }}
                    >
                        {slapText}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* === COMBO COUNTER === */}
            <AnimatePresence>
                {showComboCounter && phase === 'impact' && (
                    <motion.div
                        className="absolute top-2 right-2 flex items-center gap-1"
                        initial={{ scale: 0, y: 25 }}
                        animate={{ scale: [0, 1.3, 1], y: 0 }}
                        transition={{ delay: 0.15, type: 'spring', stiffness: 200 }}
                    >
                        <span className="text-xl text-yellow-400 font-bold">x</span>
                        <span
                            className="text-4xl font-bold text-yellow-400"
                            style={{
                                textShadow: '2px 2px 0 #B8860B, -1px -1px 0 #B8860B',
                                fontFamily: 'Impact, sans-serif',
                            }}
                        >
                            {intensity}
                        </span>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default SlapTaffyAnimation;
